import type { StudioVoicePreviewHumanizationSummary } from "./studioPreviewCenter";

export const STUDIO_HUMANIZATION_DIMENSION_MAX_SCORE = 10 as const;

export const STUDIO_HUMANIZATION_DIMENSION_ORDER = [
  "warmth",
  "professionalism",
  "repetition",
  "concision",
  "closeQuality",
  "forbiddenDrift",
] as const;

export type StudioHumanizationDimensionKey =
  (typeof STUDIO_HUMANIZATION_DIMENSION_ORDER)[number];

export type StudioHumanizationSnapshot = {
  id: string;
  label: string;
  sourceLabel: string;
  channel: "voice";
  requestId: string;
  personaLabel: string;
  transcriptExcerpt: string;
  humanization: StudioVoicePreviewHumanizationSummary;
};

export type StudioHumanizationDimensionDisplay = {
  key: StudioHumanizationDimensionKey;
  label: string;
  score: number;
  maxScore: number;
  status: "pass" | "warn" | "fail";
  detail: string;
};

export type StudioImprovementHint = {
  id: string;
  title: string;
  summary: string;
  recommendedAdjustment: string;
  rationale: string;
  severity: "review" | "guardrail";
  affectedDimensions: StudioHumanizationDimensionKey[];
  ownerControlLabel: "Owner-tunable";
};

export type StudioHumanizationComparisonDimension = {
  key: StudioHumanizationDimensionKey;
  label: string;
  before: number;
  after: number;
  delta: number;
  trend: "up" | "flat" | "down";
  detail: string;
};

export type StudioHumanizationComparison = {
  before: StudioHumanizationSnapshot;
  after: StudioHumanizationSnapshot;
  overallDelta: number;
  headline: string;
  dimensions: StudioHumanizationComparisonDimension[];
};

const DIMENSION_LABELS: Record<StudioHumanizationDimensionKey, string> = {
  warmth: "Warmth",
  professionalism: "Professionalism",
  repetition: "Repetition",
  concision: "Concision",
  closeQuality: "Close quality",
  forbiddenDrift: "Forbidden drift",
};

function dimensionStatus(score: number): "pass" | "warn" | "fail" {
  if (score >= 8) return "pass";
  if (score >= 5) return "warn";
  return "fail";
}

function dimensionDetail(params: {
  key: StudioHumanizationDimensionKey;
  score: number;
  snapshot: StudioHumanizationSnapshot;
}) {
  const status = dimensionStatus(params.score);

  if (params.key === "warmth") {
    return status === "pass"
      ? "The opening lands calm and welcoming without slipping into casual language."
      : "Add one calm acknowledgment before the next-step line so the delivery feels warmer.";
  }

  if (params.key === "professionalism") {
    return status === "pass"
      ? "The phrasing stays business-class and front-desk safe."
      : "Tighten slang-free phrasing and keep the next-step line crisp and operational.";
  }

  if (params.key === "repetition") {
    return params.snapshot.humanization.flags.repeatedSentenceCount === 0 &&
      params.snapshot.humanization.flags.repeatedStartCount === 0
      ? "Sentence starts are varied and the reply avoids robotic duplication."
      : "Reduce repeated sentence starts so the talk track sounds less robotic.";
  }

  if (params.key === "concision") {
    return status === "pass"
      ? "The preview stays brief enough for a premium front-desk interaction."
      : "Shorten the middle of the reply and let the next step do more of the work.";
  }

  if (params.key === "closeQuality") {
    return params.snapshot.humanization.flags.clearClosePresent &&
      params.snapshot.humanization.flags.politeClosePresent
      ? "The close finishes with a clear next step and courteous ending."
      : "End with one direct next step and a polite finish so the close feels complete.";
  }

  return params.snapshot.humanization.flags.forbiddenDriftPresent
    ? "The draft is drifting into blocked territory and needs a stronger staff handoff boundary."
    : "The preview stays inside front-desk scope and avoids unsafe drift.";
}

export function studioHumanizationDimensionLabel(
  key: StudioHumanizationDimensionKey
) {
  return DIMENSION_LABELS[key];
}

export function buildStudioHumanizationDimensionDisplays(
  snapshot: StudioHumanizationSnapshot
): StudioHumanizationDimensionDisplay[] {
  return STUDIO_HUMANIZATION_DIMENSION_ORDER.map((key) => {
    const score = snapshot.humanization.dimensions[key];

    return {
      key,
      label: studioHumanizationDimensionLabel(key),
      score,
      maxScore: STUDIO_HUMANIZATION_DIMENSION_MAX_SCORE,
      status: dimensionStatus(score),
      detail: dimensionDetail({
        key,
        score,
        snapshot,
      }),
    };
  });
}

export function buildStudioImprovementHints(
  snapshot: StudioHumanizationSnapshot
): StudioImprovementHint[] {
  const hints: StudioImprovementHint[] = [];
  const dimensions = snapshot.humanization.dimensions;
  const flags = snapshot.humanization.flags;

  if (dimensions.warmth <= 6 || !flags.acknowledgmentPresent) {
    hints.push({
      id: "opening_add_calm_acknowledgment",
      title: "Warm the opening",
      summary: "Start with one calm acknowledgment before the next-step sentence.",
      recommendedAdjustment:
        "Use one opener such as 'Of course, I can help with that' before moving into the next step.",
      rationale:
        "Warmth is under target, so the preview should feel more welcoming before canary review.",
      severity: "review",
      affectedDimensions: ["warmth", "professionalism"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  if (
    dimensions.repetition <= 6 ||
    flags.repeatedSentenceCount > 0 ||
    flags.repeatedStartCount > 0
  ) {
    hints.push({
      id: "repetition_reduce_duplicate_openers",
      title: "Reduce robotic repetition",
      summary: "Collapse duplicate reassurance and vary repeated sentence starts.",
      recommendedAdjustment:
        "Keep one acknowledgment, one next-step sentence, and one distinct reassurance line instead of repeating the same opener.",
      rationale:
        "Repetition is tripping deterministic QA and makes the voice preview sound mechanical.",
      severity: "review",
      affectedDimensions: ["repetition", "concision"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  if (dimensions.professionalism <= 6) {
    hints.push({
      id: "professionalism_keep_business_class_tone",
      title: "Tighten the professional tone",
      summary: "Keep the wording polished, practical, and staff-safe.",
      recommendedAdjustment:
        "Prefer one clear operational sentence over casual filler or hype language.",
      rationale:
        "Professionalism is below target and should feel more business-class before demo review.",
      severity: "review",
      affectedDimensions: ["professionalism", "concision"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  if (dimensions.concision <= 6) {
    hints.push({
      id: "concision_trim_middle",
      title: "Shorten the middle of the reply",
      summary: "Keep the answer brief and let the next step carry more weight.",
      recommendedAdjustment:
        "Cut filler from the middle sentence so the reply lands faster and cleaner.",
      rationale:
        "The preview should stay concise enough for a premium front-desk conversation.",
      severity: "review",
      affectedDimensions: ["concision", "closeQuality"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  if (
    dimensions.closeQuality <= 6 ||
    !flags.clearClosePresent ||
    !flags.politeClosePresent
  ) {
    hints.push({
      id: "close_add_next_step_and_polite_finish",
      title: "Strengthen the close",
      summary: "End with one clear next step and a courteous finish.",
      recommendedAdjustment:
        "Use one short next-step line followed by a polite ending such as 'Happy to help.'",
      rationale:
        "Close quality is below target and should feel more complete before the draft moves forward.",
      severity: "review",
      affectedDimensions: ["closeQuality", "warmth"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  if (dimensions.forbiddenDrift < STUDIO_HUMANIZATION_DIMENSION_MAX_SCORE || flags.forbiddenDriftPresent) {
    hints.push({
      id: "guardrail_reinforce_front_desk_boundary",
      title: "Reinforce the front-desk boundary",
      summary: "Keep the wording inside front-desk scope and route uncertain requests to staff.",
      recommendedAdjustment:
        "Add a brief handoff line for anything clinical, sensitive, or routing-related instead of widening the prompt.",
      rationale:
        "Style improvements never outrank medical, PHI, or live-routing guardrails.",
      severity: "guardrail",
      affectedDimensions: ["forbiddenDrift", "professionalism"],
      ownerControlLabel: "Owner-tunable",
    });
  }

  return hints;
}

function deltaDetail(params: {
  key: StudioHumanizationDimensionKey;
  delta: number;
}) {
  if (params.delta > 0) {
    return `${studioHumanizationDimensionLabel(params.key)} improved by ${params.delta} point${params.delta === 1 ? "" : "s"}.`;
  }

  if (params.delta < 0) {
    return `${studioHumanizationDimensionLabel(params.key)} regressed by ${Math.abs(params.delta)} point${Math.abs(params.delta) === 1 ? "" : "s"}.`;
  }

  return `${studioHumanizationDimensionLabel(params.key)} stayed flat.`;
}

export function buildStudioHumanizationComparison(params: {
  before: StudioHumanizationSnapshot;
  after: StudioHumanizationSnapshot;
}): StudioHumanizationComparison {
  const dimensions = STUDIO_HUMANIZATION_DIMENSION_ORDER.map((key) => {
    const before = params.before.humanization.dimensions[key];
    const after = params.after.humanization.dimensions[key];
    const delta = after - before;

    return {
      key,
      label: studioHumanizationDimensionLabel(key),
      before,
      after,
      delta,
      trend: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
      detail: deltaDetail({
        key,
        delta,
      }),
    };
  });

  const improvedLabels = dimensions
    .filter((dimension) => dimension.delta > 0)
    .sort((left, right) => right.delta - left.delta)
    .slice(0, 3)
    .map((dimension) => dimension.label.toLowerCase());
  const headline =
    improvedLabels.length > 0
      ? `The tuned draft improves ${improvedLabels.join(", ")} without widening forbidden drift.`
      : "The tuned draft keeps the humanization profile stable while preserving guardrails.";

  return {
    before: params.before,
    after: params.after,
    overallDelta:
      params.after.humanization.total.score - params.before.humanization.total.score,
    headline,
    dimensions,
  };
}
